import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Mail, Rss, Globe2, BookOpenCheck, MessageSquare, Calendar, ArrowRight, X, ChevronRight, Sparkles, Loader2, Check } from "lucide-react";
import { Link } from "react-router-dom";
import MirrorInterestsPicker, { loadMirrorPreferences, saveMirrorPreferences, DEFAULT_MIRROR_PREFS } from "@/components/MirrorInterestsPicker";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";

/**
 * WelcomeModal — V23.16.x
 *
 * Modal de bienvenue à CHAQUE connexion. 4 écrans en carrousel présentant
 * les bénéfices Noam en langue simple (sujet-verbe-complément) sans exposer
 * la mécanique interne (doctrines, graphe, fractale, IA, agents…).
 *
 * Persistance : sessionStorage `noam_welcome_dismissed` — le bandeau réapparaît
 * à chaque nouvelle connexion (le flag est effacé au login).
 * Réouverture manuelle : bouton "Pourquoi Noam ?" dans la sidebar.
 */
const SLIDES = [
  { icon: Mail, color: "#EF9F27", k: "s1" },
  { icon: Rss, color: "#7F77DD", k: "s2" },
  { icon: Globe2, color: "#378ADD", k: "s3" },
  { icon: BookOpenCheck, color: "#1D9E75", k: "s4" },
];

const OUTRO_OPTIONS = [
  { k: "opt_chat", href: "/app/chat", icon: MessageSquare, color: "#7F77DD" },
  { k: "opt_coach", href: "mailto:contact@constante-partners.com?subject=Coaching%20Noam", icon: Calendar, color: "#EF9F27", external: true },
  { k: "opt_explore", href: "/app/aujourdhui", icon: ArrowRight, color: "#1D9E75" },
];

export default function WelcomeModal({ forceOpen = false, onClose }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  // V23.15.63 — Step d'onboarding intérêts+géo (inséré entre SLIDES et OUTRO
  // uniquement si les préférences n'ont pas encore été configurées).
  const [needsMirrorSetup, setNeedsMirrorSetup] = useState(false);
  const [mirrorPrefs, setMirrorPrefs] = useState(DEFAULT_MIRROR_PREFS);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStep(0);
    } else {
      // Vision Olivier 2026-09 : le carrousel apparaît à CHAQUE connexion
      // (Google ou autre) — il rappelle les usages récurrents de NOAM
      // (emails du matin, Feed, suivi d'autres organisations, contextualisation).
      // Flag versionné : le carrousel « usages » étant un NOUVEAU contenu,
      // l'opt-out de l'ancien carrousel est réinitialisé une fois (v2).
      try {
        const never = localStorage.getItem("noam_welcome_never_v2") === "1";
        if (!never && sessionStorage.getItem("noam_welcome_dismissed") !== "1") setOpen(true);
      } catch {
        setOpen(true);
      }
    }
    // Load mirror prefs → décide si on affiche l'écran d'onboarding
    loadMirrorPreferences().then((p) => {
      if (p) {
        setMirrorPrefs(p);
        setNeedsMirrorSetup(!p.configured);
      }
    }).catch(() => {});
  }, [forceOpen]);

  const close = () => {
    setOpen(false);
    onClose?.();
    if (!forceOpen) {
      try { sessionStorage.setItem("noam_welcome_dismissed", "1"); } catch { /* silent */ }
    }
  };

  const neverShowAgain = () => {
    try { localStorage.setItem("noam_welcome_never_v2", "1"); } catch { /* silent */ }
    close();
  };

  const saveInterests = async (skip = false) => {
    setSavingPrefs(true);
    try {
      const saved = await saveMirrorPreferences(mirrorPrefs, true);
      setMirrorPrefs(saved);
      setNeedsMirrorSetup(false);
      if (!skip) notify.success(t("saas.welcome2.prefs_saved"));
      // Après save : needsMirrorSetup passe à false → outroStepIndex devient
      // SLIDES.length. On avance donc à SLIDES.length (pas SLIDES.length+1).
      setStep(SLIDES.length);
    } catch (e) {
      notify.error(fmtError(e, t("saas.welcome2.prefs_fail")));
    } finally {
      setSavingPrefs(false);
    }
  };

  if (!open) return null;

  // Structure des steps :
  //   [0..SLIDES.length-1]           → slides bénéfices
  //   SLIDES.length  (si needsMirrorSetup) → écran intérêts+géo
  //   SLIDES.length+1                → OUTRO (3 options)
  //   Si !needsMirrorSetup : SLIDES.length → OUTRO directement
  const interestsStepIndex = SLIDES.length;
  const outroStepIndex = needsMirrorSetup ? SLIDES.length + 1 : SLIDES.length;
  const isInterests = needsMirrorSetup && step === interestsStepIndex;
  const isOutro = step === outroStepIndex;
  const slide = isOutro || isInterests ? null : SLIDES[step];
  const Icon = slide?.icon;
  const color = isInterests ? "#EF9F27" : (slide?.color || "#7F77DD");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="welcome-modal"
    >
      <div
        className={`relative bg-zinc-950 rounded-2xl w-full overflow-hidden border border-white/10 shadow-2xl ${isInterests ? "max-w-2xl" : "max-w-lg"}`}
        style={{ background: `radial-gradient(circle at 20% 0%, ${color}18 0%, transparent 60%), #0A0A0F` }}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 text-zinc-500 hover:text-white p-1"
          data-testid="welcome-modal-close"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-8 pt-10 pb-6 max-h-[75vh] overflow-y-auto">
          {isInterests ? (
            <>
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                style={{ background: `${color}18`, border: `1px solid ${color}55` }}
              >
                <Sparkles className="w-6 h-6" style={{ color }} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3 leading-tight" data-testid="welcome-modal-title">
                {t("saas.welcome2.interests_t")}
              </h2>
              <p className="text-sm text-zinc-300 leading-relaxed mb-5">
                {t("saas.welcome2.interests_b")}
              </p>
              <MirrorInterestsPicker
                value={mirrorPrefs}
                onChange={setMirrorPrefs}
                mode="onboarding"
              />
            </>
          ) : !isOutro ? (
            <>
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                style={{ background: `${color}18`, border: `1px solid ${color}55` }}
              >
                <Icon className="w-6 h-6" style={{ color }} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3 leading-tight" data-testid="welcome-modal-title">
                {t(`saas.welcome2.${slide.k}t`)}
              </h2>
              <p className="text-sm text-zinc-300 leading-relaxed" data-testid="welcome-modal-body">
                {t(`saas.welcome2.${slide.k}b`)}
              </p>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-widest text-[#7F77DD] font-semibold mb-2">
                {t("saas.welcome2.welcome")}
              </div>
              <h2 className="text-2xl font-bold text-white mb-3 leading-tight">
                {t("saas.welcome2.outro_t")}
              </h2>
              <p className="text-sm text-zinc-400 mb-5">{t("saas.welcome2.outro_b")}</p>
              <div className="space-y-2">
                {OUTRO_OPTIONS.map((o) => (
                  o.external ? (
                    <a
                      key={o.k}
                      href={o.href}
                      onClick={close}
                      className="flex items-center gap-3 p-3 rounded-md border border-white/10 hover:border-white/25 transition-colors group"
                      data-testid={`welcome-modal-option-${o.k}`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${o.color}18`, border: `1px solid ${o.color}55` }}>
                        <o.icon className="w-4 h-4" style={{ color: o.color }} />
                      </div>
                      <span className="text-sm text-white flex-1">{t(`saas.welcome2.${o.k}`)}</span>
                      <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white" />
                    </a>
                  ) : (
                    <Link
                      key={o.k}
                      to={o.href}
                      onClick={close}
                      className="flex items-center gap-3 p-3 rounded-md border border-white/10 hover:border-white/25 transition-colors group"
                      data-testid={`welcome-modal-option-${o.k}`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${o.color}18`, border: `1px solid ${o.color}55` }}>
                        <o.icon className="w-4 h-4" style={{ color: o.color }} />
                      </div>
                      <span className="text-sm text-white flex-1">{t(`saas.welcome2.${o.k}`)}</span>
                      <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white" />
                    </Link>
                  )
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-8 py-4 border-t border-white/5 flex items-center justify-between">
          {/* Dots */}
          <div className="flex gap-1.5">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-colors"
                style={{ background: i === step ? color : "rgba(255,255,255,0.15)" }}
              />
            ))}
            {needsMirrorSetup && (
              <span
                className="w-1.5 h-1.5 rounded-full transition-colors"
                style={{ background: isInterests ? "#EF9F27" : "rgba(255,255,255,0.15)" }}
              />
            )}
            <span
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{ background: isOutro ? "#7F77DD" : "rgba(255,255,255,0.15)" }}
            />
          </div>
          {isInterests ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => saveInterests(true)}
                disabled={savingPrefs}
                className="text-[11px] px-3 py-1.5 rounded-full text-zinc-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-50"
                data-testid="welcome-modal-interests-skip"
              >
                {t("saas.welcome2.skip")}
              </button>
              <button
                onClick={() => saveInterests(false)}
                disabled={savingPrefs}
                className="text-xs px-4 py-1.5 rounded-full font-medium flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: color, color: "#0A0A0F" }}
                data-testid="welcome-modal-interests-save"
              >
                {savingPrefs ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {t("saas.welcome2.save_continue")}
              </button>
            </div>
          ) : !isOutro ? (
            <button
              onClick={() => setStep(step + 1)}
              className="text-xs px-4 py-1.5 rounded-full text-white font-medium transition-transform hover:scale-105 flex items-center gap-1.5"
              style={{ background: color, color: "#0A0A0F" }}
              data-testid="welcome-modal-next"
            >
              {t("saas.welcome2.continue")} <ArrowRight className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={close}
              className="text-xs px-4 py-1.5 rounded-full text-white font-medium bg-white/10 hover:bg-white/15"
              data-testid="welcome-modal-finish"
            >
              {t("saas.welcome2.close")}
            </button>
          )}
        </div>

        {!forceOpen && (
          <div className="px-8 pb-4 -mt-1 flex justify-center">
            <button
              onClick={neverShowAgain}
              className="text-[10px] text-zinc-600 hover:text-zinc-300 transition-colors underline underline-offset-2"
              data-testid="welcome-modal-never"
            >
              {t("saas.welcome2.never")}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
